import { cn } from "@base/lib/utils";
import { useQueryClient } from "@tanstack/react-query";
import { type ReactNode, useCallback, useState } from "react";
import { PullToRefreshIndicator } from "./PullToRefreshIndicator";
import { usePullToRefresh } from "./usePullToRefresh";

interface PullToRefreshScrollViewProps {
  children: ReactNode;
  /** 새로고침 시 실행할 함수. 생략하면 모든 쿼리를 refetch한다. */
  onRefresh?: () => Promise<void>;
  /** 스크롤 컨테이너에 추가할 className (padding, height 등) */
  className?: string;
  style?: React.CSSProperties;
}

export function PullToRefreshScrollView({ children, onRefresh, className, style }: PullToRefreshScrollViewProps) {
  const queryClient = useQueryClient();
  // 새로고침 후 ErrorBoundary를 리셋하기 위해 children을 강제 remount한다.
  const [contentKey, setContentKey] = useState(0);

  const handleRefresh = useCallback(async () => {
    try {
      if (onRefresh) {
        await onRefresh();
      } else {
        await queryClient.invalidateQueries({ type: "all" });
      }
    } finally {
      // 성공/실패 무관하게 contentKey를 변경해 ErrorBoundary를 리셋한다.
      setContentKey((k) => k + 1);
    }
  }, [onRefresh, queryClient]);

  const { containerRef, pullDistance, isRefreshing, isPulling } = usePullToRefresh({ onRefresh: handleRefresh });

  return (
    <div className="relative h-full">
      <PullToRefreshIndicator pullDistance={pullDistance} isRefreshing={isRefreshing} isPulling={isPulling} />
      <div ref={containerRef} className={cn("h-full overflow-auto overscroll-y-contain", className)} style={style}>
        <div key={contentKey}>{children}</div>
      </div>
    </div>
  );
}
